import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Dropdown from "react-bootstrap/Dropdown";
import Row from "react-bootstrap/Row";
import "react-quill/dist/quill.snow.css";
import { useNavigate } from "react-router-dom";
import { useGetPushNotificationQuery } from "../../graphql/generated/graphql";
import { Table } from "../Table/Table";
import {
  PushNotificationType,
  Schedule,
  TableData,
  scheduleStatus,
} from "../_shared/pushNotifications/types";
import { sortTableDataByDateTime } from "../_shared/utils";
import "./PushNotification.css";
import { createTableRowItem } from "./helper";
import { Flex, Heading } from "@aws-amplify/ui-react";
import { Spinner } from "../_shared/Loader";

export const PushNotification = () => {
  const { data, loading, error } = useGetPushNotificationQuery();
  const [pushNotifcationSchedules, setPushNotificationSchedules] = useState<
    Schedule[]
  >([]);
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [statusFilteredTableData, setStatusFilteredTableData] = useState<
    TableData[]
  >([]);
  const [filteredTableData, setFilteredTableData] = useState<any>([]);
  const [search, setSearch] = useState<string>("");
  const [isLoaded, setIsLoaded] = useState(false);

  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent<HTMLInputElement>) => {
    let filter: string = (e.target as HTMLInputElement).value;
    let dataToFilter: TableData[] = [];
    if (statusFilteredTableData.length > 0) {
      dataToFilter = statusFilteredTableData;
    } else {
      dataToFilter = tableData;
    }
    const filteredData = dataToFilter.filter((item) => {
      return (
        item.Name.toLowerCase().includes(filter.toLowerCase()) ||
        item.Title.toLowerCase().includes(filter.toLowerCase()) ||
        item.Body.toLowerCase().includes(filter.toLowerCase())
      );
    });
    setSearch((e.target as HTMLInputElement).value);
    setFilteredTableData(filteredData);
  };

  const handleStatusFilter = (value: scheduleStatus) => {
    const filteredData = tableData.filter((item) => {
      return item.Status === value;
    });
    setStatusFilteredTableData(filteredData);
    setFilteredTableData(filteredData);
  };

  const selectPushNotification = (row) => {
    const data = pushNotifcationSchedules.find((item) => {
      return row.ScheduleId === item.scheduleId;
    });

    navigate(`/push-notification/${row.id}`, {
      state: {
        success: data.numSuccess,
        failure: data.numFailure,
        optedOut: data.numOptedOut,
        status: data.status,
      },
    });
  };

  useEffect(() => {
    if (loading || error || !data) return;
    const { allSchedules, pushNotification } = data.getPushNotification;

    if (!allSchedules && !pushNotification) {
      console.log("401 Unauthorized error: Token UUID not found");
      throw new Error(
        "Sorry, something went wrong with fetching push notifications"
      );
    }

    const schedules: Schedule[] = allSchedules.schedules ?? [];
    const pushNotifcation: PushNotificationType[] = pushNotification ?? [];

    const getPushNotificationDbData = (
      scheduleId: number
    ): PushNotificationType =>
      pushNotifcation.find((i) => i.scheduleId === scheduleId);

    const createLists = schedules.reduce(
      (
        acc: { tableList: TableData[]; scheduleList: Schedule[] },
        schedule: Schedule
      ) => {
        const scheduleDb: PushNotificationType = getPushNotificationDbData(
          schedule.scheduleId
        );

        if (scheduleDb !== undefined) {
          acc.tableList.push(createTableRowItem(scheduleDb, schedule));
          acc.scheduleList.push(schedule);
        }
        return acc;
      },
      {
        tableList: [],
        scheduleList: [],
      }
    );
    const filteredData = sortTableDataByDateTime(createLists.tableList);
    setPushNotificationSchedules(createLists.scheduleList);
    setFilteredTableData(filteredData);
    setTableData(filteredData);
    setIsLoaded(true);
  }, [data, loading, error]);

  const statuses: scheduleStatus[] = [
    "COMPLETED",
    "SCHEDULED",
    "STOP",
    "PAUSE",
  ];

  const dropdownItems = statuses.map((status) => (
    <Dropdown.Item
      key={status}
      as="button"
      onClick={() => {
        handleStatusFilter(status);
      }}
    >
      {status}
    </Dropdown.Item>
  ));

  return (
    <Container style={{ padding: 25 }} fluid>
      <Flex direction="column" gap="1.5rem">
        <Heading level={2} className="screen-title">
          Push Notifications
        </Heading>
        <Row className="searchContainer">
          <Col xs={4}>
            <input
              value={search}
              onChange={handleSearch}
              className="searchBar"
            />
          </Col>
          <Col className="buttonContainer" xs={4}>
            <Dropdown className="dropDown">
              <Dropdown.Toggle variant="danger" id="status-dropdown">
                Status
              </Dropdown.Toggle>
              <Dropdown.Menu>{dropdownItems}</Dropdown.Menu>
            </Dropdown>
            <Button
              variant="secondary"
              className="searchBtn"
              onClick={() => {
                setSearch("");
                setFilteredTableData(tableData);
                setStatusFilteredTableData([]);
              }}
            >
              Reset
            </Button>
          </Col>
        </Row>

        {loading && <Spinner loading={loading} />}

        {isLoaded && (
          <div style={{ display: "flex", flex: 1 }}>
            {filteredTableData.length > 0 ? (
              <Table
                tableRows={filteredTableData}
                excludeKeys={["id"]}
                pagination={10}
                handleSelect={selectPushNotification}
              />
            ) : (
              <Container fluid>
                <Row>
                  <Col xs={4} />
                  <Col xs={4}>
                    No search results could be found for the values you entered.
                  </Col>
                  <Col xs={2} />
                </Row>
              </Container>
            )}
          </div>
        )}
      </Flex>
    </Container>
  );
};
